import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Loader2, Monitor, MoreHorizontal, Pencil, Plus, RefreshCw, Save, Share2, Star, Trash2, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { CompanySearch } from '@/components/CompanySearch';
import { useBoard, useBoards, useDeleteBoard, useDuplicateBoard, useFavouriteBoard, useMetricCatalogue, useSaveBoard, type Card, type Dashboard, type DashboardDefinition } from '@/api/boards';
import { BoardGrid, useBoardClient } from '@/components/boards/BoardGrid';
import { CardEditor } from '@/components/boards/CardEditor';
import { ShareDialog } from '@/components/boards/ShareDialog';
import { nextY } from '@/lib/boards';
import { ROUTES, dashboardDisplayPath, dashboardPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * A custom dashboard (`/dashboards/:id`). View mode refreshes on the dashboard's interval; Edit
 * mode (owner or "can edit") drags/resizes cards on a 12-column grid, adds/edits cards, and saves
 * with a version check so two editors can't overwrite each other.
 */

const REFRESH_OPTIONS = [30, 60, 120, 300, 600, 1800];
const fmtRefresh = (s: number) => (s < 60 ? `${s} s` : `${s / 60} min`);

export default function DashboardPage() {
  const id = useParams<{ id: string }>().id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error } = useBoard(id);
  const list = useBoards();
  const catalogue = useMetricCatalogue();
  const save = useSaveBoard();
  const del = useDeleteBoard();
  const dup = useDuplicateBoard();
  const fav = useFavouriteBoard();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ name: string; description: string | null; definition: DashboardDefinition } | null>(null);
  const [cardDialog, setCardDialog] = useState<{ open: boolean; card: Card | null }>({ open: false, card: null });
  const [shareOpen, setShareOpen] = useState(false);

  const d = data?.dashboard;
  const access = data?.access;
  const listItem = list.data?.dashboards.find((x) => x.id === id);
  const shown = editing && draft ? draft : d ? { name: d.name, description: d.description, definition: d.definition } : null;
  const dirty = !!(editing && draft && d && JSON.stringify(draft) !== JSON.stringify({ name: d.name, description: d.description, definition: d.definition }));
  const client = useBoardClient(shown?.definition);

  // A brand-new (empty) dashboard opens straight into edit mode.
  useEffect(() => {
    if (d && access?.canEdit && d.definition.cards.length === 0 && !editing) startEdit(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.id]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  useEffect(() => {
    if (!d) return;
    const before = document.title;
    document.title = `${d.name} · Cosmos`;
    return () => { document.title = before; };
  }, [d?.name]);

  function startEdit(x: Dashboard) {
    setDraft({ name: x.name, description: x.description, definition: structuredClone(x.definition) });
    setEditing(true);
  }
  const setDef = (patch: Partial<DashboardDefinition>) => setDraft((x) => (x ? { ...x, definition: { ...x.definition, ...patch } } : x));
  const doSave = () => {
    if (!d || !draft) return;
    save.mutate({ id: d.id, version: d.version, ...draft }, {
      onSuccess: () => { setEditing(false); setDraft(null); toast.success('Dashboard saved'); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to save'),
    });
  };

  const metrics = catalogue.data?.metrics ?? [];
  const cards = shown?.definition.cards ?? [];
  const canEdit = !!access?.canEdit;

  return (
    <div className="w-full px-4 py-4 md:px-6">
      <Link to={ROUTES.dashboards} className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Dashboards
      </Link>
      {isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading dashboard…</div>
      ) : error || !d || !shown ? (
        <div className="mt-4 text-sm text-destructive">{error instanceof Error ? error.message : 'Dashboard not found'}</div>
      ) : (
        <>
          {/* Header */}
          <div className="flex flex-wrap items-start gap-3">
            <div className="min-w-0 flex-1">
              {editing && draft ? (
                <div className="space-y-1">
                  <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={120} aria-label="Dashboard name"
                    className="h-9 w-full max-w-lg rounded-md border border-border bg-background px-2 text-lg font-semibold text-foreground outline-none focus:border-ring" />
                  <input value={draft.description ?? ''} onChange={(e) => setDraft({ ...draft, description: e.target.value || null })} maxLength={500} placeholder="Description (optional)"
                    className="h-8 w-full max-w-lg rounded-md border border-border bg-background px-2 text-sm text-foreground outline-none focus:border-ring" />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <h1 className="truncate text-xl font-semibold text-foreground">{d.name}</h1>
                    <button type="button" onClick={() => fav.mutate({ id: d.id, on: !listItem?.favourite })} title={listItem?.favourite ? 'Unstar (removes it from the sidebar)' : 'Star (adds it to the sidebar)'}
                      className="text-muted-foreground hover:text-warning"><Star className={cn('h-4 w-4', listItem?.favourite && 'fill-warning text-warning')} /></button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {d.description && <>{d.description} · </>}
                    {access?.isOwner ? 'Yours' : `By ${d.ownerName}`}
                    {d.everyone !== 'none' && ` · shared with everyone (${d.everyone})`}
                    {client.name ? <> · <span className="font-medium text-foreground">{client.name}</span></> : ' · all clients'}
                  </p>
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {editing ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => setCardDialog({ open: true, card: null })}><Plus className="h-3.5 w-3.5" /> Add card</Button>
                  <Button size="sm" variant="ghost" onClick={() => { if (!dirty || confirm('Discard your changes?')) { setEditing(false); setDraft(null); } }}><X className="h-3.5 w-3.5" /> Cancel</Button>
                  <Button size="sm" onClick={doSave} disabled={save.isPending || !draft?.name.trim()}>
                    {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="ghost" title="Refresh now" onClick={() => qc.invalidateQueries({ queryKey: ['boards', 'values'] })}><RefreshCw className="h-3.5 w-3.5" /></Button>
                  {canEdit && <Button size="sm" variant="outline" onClick={() => startEdit(d)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>}
                  <Button size="sm" variant="outline" onClick={() => setShareOpen(true)}><Share2 className="h-3.5 w-3.5" /> Share</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(dashboardDisplayPath(d.id))} title="Full screen, for a wall display"><Monitor className="h-3.5 w-3.5" /> Display</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" aria-label="More"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => dup.mutate({ id: d.id }, { onSuccess: (r) => { toast.success('Copied to your dashboards'); navigate(dashboardPath(r.dashboard.id)); } })}>
                        <Copy className="mr-2 h-3.5 w-3.5" /> Duplicate
                      </DropdownMenuItem>
                      {access?.isOwner && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => {
                            if (confirm(`Delete “${d.name}”? Anyone it's shared with loses it too.`)) del.mutate(d.id, { onSuccess: () => { toast.success('Dashboard deleted'); navigate(ROUTES.dashboards); } });
                          }}><Trash2 className="mr-2 h-3.5 w-3.5" /> Delete</DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </div>

          {/* Edit-mode settings bar */}
          {editing && draft && (
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2 text-xs">
              <label className="flex items-center gap-2 text-muted-foreground">Client
                <select value={draft.definition.client.mode} onChange={(e) => setDef({ client: { mode: e.target.value as DashboardDefinition['client']['mode'], companyId: null, companyName: null } })}
                  className="h-7 rounded-md border border-border bg-background px-1 text-xs text-foreground">
                  <option value="picker">Follow the header client picker</option>
                  <option value="all">Always all clients</option>
                  <option value="fixed">Always one client…</option>
                </select>
              </label>
              {draft.definition.client.mode === 'fixed' && (
                <div className="w-64">
                  <CompanySearch value={draft.definition.client.companyId != null ? { id: draft.definition.client.companyId, name: draft.definition.client.companyName ?? '' } : null}
                    onChange={(c) => setDef({ client: { mode: 'fixed', companyId: c?.id ?? null, companyName: c?.name ?? null } })} placeholder="Which client?" />
                </div>
              )}
              <label className="flex items-center gap-2 text-muted-foreground">Refresh every
                <select value={draft.definition.refreshSeconds} onChange={(e) => setDef({ refreshSeconds: Number(e.target.value) })}
                  className="h-7 rounded-md border border-border bg-background px-1 text-xs text-foreground">
                  {REFRESH_OPTIONS.map((s) => <option key={s} value={s}>{fmtRefresh(s)}</option>)}
                </select>
              </label>
              <span className="text-muted-foreground">Drag a card by its title; resize from the bottom-right corner.</span>
            </div>
          )}

          <div className="mt-4">
            {cards.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border px-4 py-16 text-center text-sm text-muted-foreground">
                This dashboard has no cards yet.
                {canEdit && <Button size="sm" onClick={() => { if (!editing) startEdit(d); setCardDialog({ open: true, card: null }); }}><Plus className="h-3.5 w-3.5" /> Add a card</Button>}
              </div>
            ) : (
              <BoardGrid def={shown.definition} editing={editing}
                onLayout={(next) => setDef({ cards: next })}
                onEditCard={(c) => setCardDialog({ open: true, card: c })} />
            )}
          </div>

          <CardEditor open={cardDialog.open} onOpenChange={(o) => setCardDialog((s) => ({ ...s, open: o }))} card={cardDialog.card} metrics={metrics}
            nextY={nextY(cards)}
            onSave={(c) => setDef({ cards: cards.some((x) => x.id === c.id) ? cards.map((x) => (x.id === c.id ? c : x)) : [...cards, c] })}
            onDelete={(cid) => setDef({ cards: cards.filter((x) => x.id !== cid) })} />
          <ShareDialog open={shareOpen} onOpenChange={setShareOpen} dashboard={d} isOwner={!!access?.isOwner} />
        </>
      )}
    </div>
  );
}

